import { useEffect, useState } from "react";
import { Link2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { BLOG_SHARE_UI as UI, blogShareLinks } from "@/lib/blog-share";
import { absoluteUrl } from "@/lib/seo";
import { FacebookIcon, TelegramIcon, WhatsAppIcon, XIcon } from "@/components/public/social-icons";

type Method = "native" | "whatsapp" | "facebook" | "telegram" | "x" | "copy";

const BTN = "min-h-11 rounded-full px-5";

export function BlogShare({ title, slug }: { title: string; slug: string }) {
  const url = absoluteUrl(`/blog/${slug}`);
  const links = blogShareLinks(title, url);
  const [canNative, setCanNative] = useState(false);
  useEffect(() => {
    setCanNative(typeof navigator !== "undefined" && typeof navigator.share === "function");
  }, []);

  const track = (method: Method) => {
    const g = (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag;
    if (typeof g === "function") g("event", "share", { method, content_type: "article", item_id: slug });
  };

  const networks = [
    { method: "whatsapp" as const, href: links.whatsapp, label: UI.whatsapp, Icon: WhatsAppIcon },
    { method: "facebook" as const, href: links.facebook, label: UI.facebook, Icon: FacebookIcon },
    { method: "telegram" as const, href: links.telegram, label: UI.telegram, Icon: TelegramIcon },
    { method: "x" as const, href: links.x, label: UI.x, Icon: XIcon },
  ];

  return (
    <>
      <p className="font-display text-base font-bold text-dark-brown">{UI.label}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {canNative && (
          <Button variant="secondary" className={BTN} onClick={() => {
            track("native");
            navigator.share({ title, url }).catch(() => { /* AbortError and others ignored */ });
          }}>
            <Share2 aria-hidden />{UI.native}
          </Button>
        )}
        {networks.map(({ method, href, label, Icon }) => (
          <Button key={method} asChild variant="secondary" className={BTN}>
            <a href={href} target="_blank" rel="noopener noreferrer" onClick={() => track(method)}>
              <Icon aria-hidden className="size-4" />{label}
            </a>
          </Button>
        ))}
        <Button variant="secondary" className={BTN} onClick={async () => {
          track("copy");
          try {
            await navigator.clipboard.writeText(url);
            toast.success(UI.copied);
          } catch { /* Never log the URL. */ }
        }}>
          <Link2 aria-hidden />{UI.copy}
        </Button>
      </div>
    </>
  );
}
